<script setup lang="ts">
import { useLicense, VPdfViewer, type VPVInstance } from "@vue-pdf-viewer/viewer";
import { useDark } from "@vueuse/core";
import { computed, nextTick, onBeforeMount, onBeforeUnmount, ref, watch } from "vue";
import loading from "@/components/common/loading.vue";
// The viewer ships a pdfjs 4.10.38 worker but resolves the project's hoisted
// pdfjs-dist 5.4.296 API on the main thread, which throws a worker/API version
// mismatch. Force the worker to the matching 5.4.296 build.
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

const isDark = useDark();

const PDF_TOKEN = import.meta.env.VITE_VPV_LICENSE ?? "Your VPV Domain Token";

onBeforeMount(() => {
  // Initialize the license here to ensure it's set up before the component renders.
  useLicense(PDF_TOKEN);
});
const props = withDefaults(
  defineProps<{
    filePath: string;
    scale: number;
    /** Download name without or with .pdf — required because src is a blob URL (UUID, no extension). */
    fileName?: string;
    variant?: "modal" | "inline";
  }>(),
  {
    fileName: "document",
    variant: "modal",
  },
);

// Fetch the PDF ourselves with a plain GET (same call the download button uses)
// and expose it as a local blob URL. The presigned S3 URL is signed for GET only,
// so letting the viewer probe it with its own HEAD/range request fails (403 + CORS).
// A blob URL is a non-empty string (which the viewer requires) and is served
// locally, so no extra network probe is made.
const localUrl = ref<string | null>(null);

const downloadFilename = computed(() => {
  const name = props.fileName.trim() || "document";
  return name.toLowerCase().endsWith(".pdf") ? name : `${name}.pdf`;
});

const revokeLocalUrl = () => {
  if (localUrl.value) {
    URL.revokeObjectURL(localUrl.value);
    localUrl.value = null;
  }
};

const loadPdf = async (url: string) => {
  revokeLocalUrl();
  const response = await fetch(url);
  const blob = await response.blob();
  // Force PDF MIME so download/open works even if S3 Content-Type is missing/wrong.
  const pdfBlob =
    blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
  localUrl.value = URL.createObjectURL(pdfBlob);
};

watch(
  () => props.filePath,
  (url) => {
    if (url) loadPdf(url);
  },
  { immediate: true },
);

const pdfViewerRef = ref<VPVInstance>();

const applyScale = async () => {
  await nextTick();
  pdfViewerRef.value?.zoomControl?.zoom(props.scale, { immediate: true });
};

watch(() => props.scale, applyScale);

const onPdfLoaded = () => {
  applyScale();
};

onBeforeUnmount(revokeLocalUrl);
</script>

<template>
  <div class="relative w-full h-full" :class="variant === 'inline' ? 'min-h-0' : 'min-h-[80vh]'">
    <VPdfViewer
      v-if="localUrl"
      ref="pdfViewerRef"
      :src="localUrl"
      :worker-url="pdfWorkerUrl"
      :dark-Mode="isDark"
      :initial-scale="scale"
      :download-filename="downloadFilename"
      @loaded="onPdfLoaded"
    />
    <div v-else class="absolute inset-0 flex items-center justify-center">
      <loading class="text-4xl" />
    </div>
  </div>
</template>
